import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { standarize } from '../utils/importerUtils'
import { CsvColumn, CsvImportResult, CsvRow } from "../types"
import { FieldType, FileType, O } from '../../ui'
import { isDefined } from '../../../utils/typeUtils'
import { fileHelpers } from '../../files'

// Clave que se agrega a cada fila leída con su número de fila en el archivo
export const FILE_ROW_KEY = 'fileRow'

export const useImportCsv = () => {
    const { toFileType } = fileHelpers

    return (
        file: File,
        columns: { [key: string]: CsvColumn },
        importData: (rows: CsvRow[]) => Promise<CsvImportResult>
    ) => {
        const fileType = toFileType(file.type)
        const isCsv = fileType === FileType.CSV

        const readRow = <T>(row: any, column: CsvColumn): T | null => {
            const rawValue = row[standarize(column.name)]
            if (!isDefined(rawValue) || rawValue === '') return null

            if (column.type === FieldType.NUMBER || column.type === FieldType.PERCENTAGE) {
                // Excel ya entrega los números como número (los porcentajes, como fracción)
                if (typeof rawValue === 'number') return rawValue as any as T
                const text = rawValue.toString()
                const num = parseFloat(text.replace(/[^0-9.-]/g, ''))
                if (isNaN(num)) return null
                return (text.includes('%') ? num / 100 : num) as any as T
            }
            return rawValue.toString() as T
        }

        // fileRows: número de fila de cada registro en el archivo (como se ve en Excel), para mostrar en los reportes
        const buildRows = (rowsData: any[], fileRows: number[]) => {
            return rowsData.map((row, index) => {
                const standarizedRow = Object.keys(row).reduce((acc, key) => {
                    acc[standarize(key)] = row[key]
                    return acc
                }, {} as any)

                return Object.entries(columns).reduce((acc, [columnKey, column]) => {
                    acc[columnKey] = readRow(standarizedRow, column)
                    return acc
                }, { [FILE_ROW_KEY]: fileRows[index] } as Record<string, any>)
            })
        }

        // Las plantillas pueden tener filas de información arriba de la tabla: la cabecera es la fila
        // (entre las primeras) con más nombres de columnas conocidos. Si no hay ninguna, se usa la primera.
        const findHeaderRow = (rows: any[][]): number => {
            const columnNames = Object.values(columns).map(column => standarize(column.name))
            let headerRow = 0
            let maxMatches = 0
            rows.slice(0, 20).forEach((row, index) => {
                const matches = row.filter(cell => isDefined(cell) && columnNames.includes(standarize(cell.toString()))).length
                if (matches > maxMatches) {
                    headerRow = index
                    maxMatches = matches
                }
            })
            return headerRow
        }

        const load = async (e: any): Promise<CsvRow[]> => {
            let rowsData: any[]
            let fileRows: number[]
            // Datos generales de la plantilla de listas de precios (nombre y ajuste general)
            let listName: O<string>
            let generalYield: O<number>
            if (isCsv) {
                const data = e.target?.result as string
                const lines = data.split(/\r?\n/)
                const previewRows = Papa.parse<string[]>(lines.slice(0, 20).join('\n')).data
                const headerRow = findHeaderRow(previewRows)
                // Sin skipEmptyLines para conservar la correspondencia registro <-> línea; las vacías se filtran después
                const result = Papa.parse<any>(lines.slice(headerRow).join('\n'), { header: true })
                const isEmptyRow = (row: Record<string, any>) => Object.values(row).every(value => !isDefined(value) || value.toString().trim() === '')
                const parsedRows = result.data
                    .map((row, index) => ({ row, fileRow: headerRow + index + 2 }))
                    .filter(({ row }) => !isEmptyRow(row))
                rowsData = parsedRows.map(({ row }) => row)
                fileRows = parsedRows.map(({ fileRow }) => fileRow)
            } else {
                const data = e.target?.result as ArrayBuffer
                const workbook = XLSX.read(data, { type: 'array' })
                const sheetName = workbook.SheetNames[0]
                const sheet = workbook.Sheets[sheetName]
                listName = sheet['B1']?.v
                const yieldValue = sheet['B3']?.v
                // Con formato de porcentaje Excel guarda la fracción (10% = 0,1)
                generalYield = typeof yieldValue === 'number' && yieldValue !== 0 && Math.abs(yieldValue) <= 1 ? yieldValue * 100 : yieldValue
                const sheetRows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '', blankrows: true })
                // sheet_to_json numera las filas desde el inicio del rango usado de la hoja, no desde A1
                const firstSheetRow = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.r : 0
                const headerRow = firstSheetRow + findHeaderRow(sheetRows)
                rowsData = XLSX.utils.sheet_to_json(sheet, { defval: '', range: headerRow })
                // SheetJS agrega a cada registro __rowNum__ (fila de la hoja, base 0)
                fileRows = rowsData.map(row => row.__rowNum__ + 1)
            }

            return buildRows(rowsData, fileRows).map(row => ({
                ...row,
                "metadata_listName": listName || row["nombre"] || "Nueva Lista",
                "metadata_yield": generalYield || row["rendimiento"] || 0
            }))
        }

        return new Promise<CsvImportResult>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = async (e) => {
                try {
                    const rows = await load(e)
                    const result = importData(rows)
                    resolve(result)
                } catch (error) {
                    reject(error)
                }
            }
            reader.onerror = () => reject(new Error('Failed to read file'))
            if (isCsv) {
                reader.readAsText(file)
            } else {
                reader.readAsArrayBuffer(file)
            }
        })
    }
}
